// Form Submission Service
import {apiClient} from './api-client';
import type {FormSubmissionData, PublicFormData, SubmissionResponse} from '../types/form-submission.types';
import {FormTranslations} from "@services/translations.service.ts";

export const formSubmissionService = {
  // Get public form data for rendering
  async getPublicForm(formId: number | string, token?: string): Promise<PublicFormData> {
    const params: any = { formId };
    if (token) {
      params.token = token;
    }
    return apiClient.callProxy('formSubmission', 'getPublicForm', params);
  },

  // Get public form by SEF alias
  async getPublicFormByAlias(alias: string): Promise<PublicFormData> {
    return apiClient.callProxy('formSubmission', 'getPublicFormByAlias', { alias });
  },

  /**
   * Get all translations for a form (including fields and sections)
   */
  async getFormTranslations(
    formId: number,
    languageCode?: string
  ): Promise<FormTranslations> {
    const params: any = { form_id: formId };

    if (languageCode) {
      params.language_code = languageCode;
    }

    return apiClient.callProxy('formSubmission', 'getFormTranslations', params);
  },

  // Submit form data
  async submitForm(data: FormSubmissionData): Promise<SubmissionResponse> {
    // Check if there are any ACTUAL file uploads in the data (not just empty file fields)
    const hasFiles = Object.entries(data.fields).some(([_fieldId, value]) => {
      if (!value) return false; // Skip null/undefined

      if (Array.isArray(value)) {
        return value.some(item => item?.file instanceof File);
      }

      return value?.file instanceof File;
    });

    // If there are files, use FormData instead of JSON
    if (hasFiles) {
      const formData = new FormData();

      // Add form ID
      formData.append('formId', data.formId.toString());

      // Process fields
      const fieldsData: Record<string, any> = {};
      Object.entries(data.fields).forEach(([fieldId, value]) => {
        // Skip null, undefined, or empty objects
        if (!value) return;
        if (typeof value === 'object' && !Array.isArray(value) && !value.file && Object.keys(value).length === 0) {
          return;
        }

        if (Array.isArray(value)) {
          // Handle multiple files or array values
          const hasFileObjects = value.some(item => item?.file instanceof File);
          if (hasFileObjects) {
            // Add files to FormData
            value.forEach((item) => {
              if (item?.file instanceof File) {
                formData.append(`${fieldId}[]`, item.file);
              }
            });
            // Don't add to fieldsData - files are sent separately
          } else {
            // Regular array field (like checkbox)
            fieldsData[fieldId] = value;
          }
        } else if (value?.file instanceof File) {
          // Handle single file
          formData.append(fieldId, value.file);
          // Don't add to fieldsData - file is sent separately
        } else {
          // Regular field
          fieldsData[fieldId] = value;
        }
      });

      // Add processed fields as JSON
      formData.append('fields', JSON.stringify(fieldsData));

      // Add metadata
      if (data.metadata) {
        formData.append('metadata', JSON.stringify(data.metadata));
      }
      if ((data as any).sessionId) {
        formData.append('sessionId', (data as any).sessionId);
      }

      return apiClient.callProxy('formSubmission', 'submitForm', formData, 'POST');
    }

    // No files - use regular JSON submission
    return apiClient.callProxy('formSubmission', 'submitForm', data, 'POST');
  },

  // Save draft submission
  async saveDraft(formId: number, data: Record<string, any>): Promise<{ success: boolean; draftId: string }> {
    return apiClient.callProxy('formSubmission', 'saveDraft', { formId, data }, 'POST');
  },

  // Load draft submission
  // Can be called with either draftId (load specific draft) or formId (find user's draft)
  async loadDraft(params: { draftId?: string; formId?: number }): Promise<{ formId: number; data: Record<string, any> } | null> {
    return apiClient.callProxy('formSubmission', 'loadDraft', params);
  },

  // Upload file for form field
  async uploadFile(formId: number, fieldId: string, file: File): Promise<{ fileId: string; url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('formId', formId.toString());
    formData.append('fieldId', fieldId);

    return apiClient.callProxy('formSubmission', 'uploadFile', formData, 'POST');
  },

  // Validate field value
  async validateField(formId: number, fieldId: string, value: any): Promise<{ valid: boolean; error?: string }> {
    return apiClient.callProxy('formSubmission', 'validateField', { formId, fieldId, value });
  },

  // Check form availability
  async checkFormAvailability(formId: number): Promise<{
    available: boolean;
    message?: string;
    reason?: 'closed' | 'expired' | 'limit-reached' | 'unpublished';
  }> {
    return apiClient.callProxy('formSubmission', 'checkAvailability', { formId });
  },

  // Get form integrations
  async getFormIntegrations(formId: number): Promise<any[]> {
    return apiClient.callProxy('formSubmission', 'getFormIntegrations', { formId });
  },

  // Update payment status after frontend confirmation
  async updatePaymentStatus(submissionId: number, paymentStatus: string, paymentIntentId?: string): Promise<{ success: boolean; message: string }> {
    return apiClient.callProxy('formSubmission', 'updatePaymentStatus', {
      submissionId,
      paymentStatus,
      paymentIntentId
    }, 'POST');
  },
};
